import { ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import Card from "@rio-cloud/rio-uikit/Card";
import DatePicker from "@rio-cloud/rio-uikit/DatePicker";
import Dialog from "@rio-cloud/rio-uikit/Dialog";
import { Bar, Line, Notification, Select, SelectOption } from "@rio-cloud/rio-uikit";
import ComposedChart from "@rio-cloud/rio-uikit/ComposedChart";
import ChartTooltip from "@rio-cloud/rio-uikit/ChartTooltip";
import moment from "moment";
import { useIntl } from "react-intl";
import TableFueling from "@components/Table/Fueling";
import { AlertsRecentCard, MonthNavigation } from "@components/Dashboard";
import SelectedFilters from "@components/SelectedFilters";
import KpiCardsSkeleton from "@components/Skeletons/KpiCardsSkeleton";
import FuelMixChartSkeleton from "@components/Skeletons/FuelMixChartSkeleton";
import Top5VehiclesTableSkeleton from "@components/Skeletons/Top5VehiclesTableSkeleton";
import DetailFormSkeleton from "@components/Skeletons/DetailFormSkeleton";
import { FuelingRecord, FuelingSummary } from "@components/Table/Fueling/mock";
import { RecentAlertsSummary } from "@components/Dashboard/AlertsRecentCard";
import {
  getFuelingData,
  getFuelingSummary,
  exportFueling,
  getFuelingEfficiency,
  getFuelingAlertTotals,
} from "@services/fueling";
import { getDetailTransaction, getFile } from "@services/form";
import { setEndDate, setStartDate, useGetStoredEndDate, useGetStoredStartDate } from "@store/date/dateSlice";
import { useAppDispatch } from "@store";
import { useIsTreeReady } from "@store/tmsAssetTree/tmsAssetTreeSlice";
import { useT } from "@hooks";
import { toOptionalListFilters, useRequestFilters, useStoredFilterValues } from "@hooks/useStoredFilters";
import { useMonthNavigation } from "@hooks/useMonthNavigation";
import KpiCard, { KpiCardDelta, KpiCardIcon } from "@components/Dashboard/KPICards/KpiCard";
import KpiInfoTooltip, { formatPeriodRange } from "@components/shared/KpiInfoTooltip";
import KpiComparisonTooltip from "@components/shared/KpiComparisonTooltip";

const PAGE_SIZE = 10;

const formatKpiComparisonCurrency = (value: number) =>
  `R$ ${value.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const formatKpiCurrency = (value?: number) =>
  (value ?? 0).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const FUEL_UNIT_MAP: Record<string, string> = {
  biodiesel: "L",
  diesel: "L",
  etanol: "L",
  gasolina: "L",
  eletrico: "kWh",
  gnv: "m³",
};

const FUEL_ORDER = ["biodiesel", "diesel", "etanol", "eletrico", "gasolina", "gnv"];

const BAR_COLOR_MAP: Record<string, string> = {
  biodiesel: "#CCECCE",
  diesel: "#93D5DC",
  etanol: "#2A374050",
  eletrico: "#FF8E3C50",
  gasolina: "#1E40AF50",
  gnv: "#4B80A6",
};

interface FuelMixDataItem {
  name: string;
  key: string;
  expense: number;
  volume: number;
  unit: string;
  avgPrice: number;
  fill: string;
}

type Translate = ReturnType<typeof useT>;

const resolveMomentLocale = (currentLocale: string) => {
  if (currentLocale.startsWith("en")) return "en-gb";
  if (currentLocale.startsWith("es")) return "es";
  return "pt-br";
};

const resolveDriverLabelKey = (index: number) => {
  if (index === 0) return "fueling.form.driver1";
  if (index === 1) return "fueling.form.driver2";
  return "fueling.form.driver3";
};

const mapPaymentLabel = (method: string): string => {
  const map: Record<string, string> = {
    CASH: "dinheiro",
    CREDIT: "credito",
    DEBIT: "debito",
    PIX: "pix",
  };
  return map[method.toUpperCase()] ?? method.toLowerCase();
};

const formatCurrencyValue = (value?: number | null): string => {
  if (value == null) return "-";
  return value.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

interface FuelingDetailFormProps {
  data: any;
  momentLocale: string;
  transactionId: string | null;
  onDownloadFile: (transactionId: string) => void;
}

const FuelingDetailForm = ({ data, momentLocale, transactionId, onDownloadFile }: Readonly<FuelingDetailFormProps>) => {
  const t = useT();
  const PAYMENT_METHODS: SelectOption[] = [
    { id: "credito", label: t("fueling.paymentMethod.credit") },
    { id: "debito", label: t("fueling.paymentMethod.debit") },
    { id: "dinheiro", label: t("fueling.paymentMethod.cash") },
    { id: "pix", label: t("fueling.paymentMethod.pix") },
  ];

  const d = data;
  const noop = () => {};
  const catId = String(d.categoryId ?? d.category ?? "");
  const subId = String(d.subcategoryId ?? d.subcategory ?? "");
  const branchId = String(d.branchId ?? d.branch ?? "");
  const segId = String(d.segmentBussinessId ?? d.segment ?? "");
  const supId = String(d.supplierClientId ?? d.supplier ?? "");
  const vehId = String(d.vehicleId ?? d.vehicle ?? "");
  const driverIds = d.driverIds || (d.driverId ? [d.driverId] : []);
  const driverParts = d.driverName ? d.driverName.split(",") : [];
  const driverNames = driverParts.map((parte: string, indice: number) => {
    const semEspacoInicial = indice > 0 ? parte.trimStart() : parte;
    return indice < driverParts.length - 1 ? semEspacoInicial.trimEnd() : semEspacoInicial;
  });
  const paymentMethod = d.supplyTrucker?.paymentMethod ?? d.paymentMethod ?? "";
  const startDt = d.supplyTrucker?.startDateTime ?? d.startDateTime ?? d.transactionDate;
  const stopLocation = d.supplyTrucker?.stopLocation ?? d.stopLocation;
  const odometer = d.supplyTrucker?.odometer ?? d.odometer;
  const fuelVolume = d.supplyTrucker?.fuelVolume ?? d.fuelVolume ?? d.quantity;
  const fuelUnit = d.supplyTrucker?.fuelUnit ?? d.fuelUnit ?? "L";
  const fuelUnitPrice = d.supplyTrucker?.fuelUnitPrice ?? d.fuelUnitPrice ?? d.unitPrice;
  const complements = d.supplyTrucker?.complementsTransaction ?? d.complements ?? [];

  return (
    <div className="padding-x-20">
      <form>
        <fieldset>
          <div className="">
            <div className="form-group">
              <label className="margin-0 margin-bottom-5" htmlFor="category">
                {t("expense.preform.category")}
              </label>
              <Select
                id="category"
                disabled
                options={[{ id: catId || "detail-cat", label: d.categoryName || "-" }]}
                value={[catId || "detail-cat"]}
                onChange={noop}
                placeholder={t("common.placeholder.select")}
                className="rounded-small flex-1-1"
              />
            </div>
            <div className="form-group">
              <label className="margin-0 margin-bottom-5" htmlFor="subcategory">
                {t("expense.preform.subcategory")}
              </label>
              <Select
                id="subcategory"
                disabled
                options={[{ id: subId || "detail-sub", label: d.subcategoryName || "-" }]}
                value={[subId || "detail-sub"]}
                onChange={noop}
                placeholder={t("common.placeholder.select")}
                className="rounded-small flex-1-1"
              />
            </div>

            <div className="display-flex flex-wrap gap-15">
              <div className="flex-1-1 form-group width-100pct-xs">
                <label htmlFor="startDate">{t("fueling.form.startDate")}</label>
                <DatePicker
                  id="startDate"
                  dateFormat="DD/MM/YYYY"
                  locale={momentLocale}
                  onChange={noop}
                  value={startDt ? moment(startDt).format("DD/MM/YYYY") : ""}
                  className="margin-0"
                  timeFormat={false}
                  inputProps={{ readOnly: true, disabled: true }}
                />
              </div>
              <div className="flex-1-1 form-group width-100pct-xs">
                <label htmlFor="startTime">{t("fueling.form.startTime")}</label>
                <div className="input-group">
                  <span className="input-group-addon">
                    <span className="rioglyph rioglyph-time" />
                  </span>
                  <input
                    id="startTime"
                    type="text"
                    className="form-control"
                    readOnly
                    value={startDt ? moment(startDt).format("HH:mm") : "-"}
                  />
                </div>
              </div>
            </div>
            <div className="form-group">
              <label htmlFor="stopLocation">{t("fueling.form.stopLocation")}</label>
              <input id="stopLocation" type="text" className="form-control" readOnly value={stopLocation || "-"} />
            </div>
            <div className="form-group">
              <label htmlFor="odometerKm">{t("fueling.form.odometer")}</label>
              <div className="input-group">
                <span className="input-group-addon">Km</span>
                <input id="odometerKm" type="text" className="form-control" readOnly value={odometer ?? "-"} />
              </div>
            </div>
            <div className="form-group">
              <label>{t("fueling.form.volume")}</label>
              <div className="input-group">
                <span className="input-group-addon">{fuelUnit}</span>
                <input type="text" className="form-control" readOnly value={fuelVolume ?? "-"} />
              </div>
            </div>
            <div className="form-group">
              <label htmlFor="inputValue">{t("fueling.form.totalFuelValue")}</label>
              <div className="input-group">
                <span className="input-group-addon">R$</span>
                <input
                  id="inputValue"
                  type="text"
                  className="form-control"
                  readOnly
                  value={formatCurrencyValue(d.amount)}
                />
              </div>
            </div>
            <div className="form-group">
              <label htmlFor="fuelUnitPrice">{t("fueling.form.unitFuelValue")}</label>
              <div className="input-group">
                <span className="input-group-addon">R$</span>
                <input
                  id="fuelUnitPrice"
                  type="text"
                  className="form-control"
                  readOnly
                  value={formatCurrencyValue(fuelUnitPrice)}
                />
              </div>
            </div>

            {complements.length > 0 && (
              <div className="margin-bottom-20">
                <div className="text-color-primary text-size-16 text-medium margin-bottom-15">
                  {t("fueling.form.complementSection")}
                </div>
                {complements.map((complement: any, index: number) => (
                  <div
                    key={`${complement.type}-${complement.volume}-${complement.value}-${complement.unitValue}`}
                    className="padding-15 border border-color-light rounded-small margin-bottom-15"
                  >
                    <div className="form-group">
                      <label>{t("fueling.form.complementType")}</label>
                      <input type="text" className="form-control" readOnly value={complement.type || "-"} />
                    </div>
                    <div className="form-group">
                      <label>{t("fueling.form.volume")}</label>
                      <div className="input-group">
                        <span className="input-group-addon">L</span>
                        <input type="text" className="form-control" readOnly value={complement.volume ?? "-"} />
                      </div>
                    </div>
                    <div className="form-group">
                      <label>{t("fueling.form.complementValue")}</label>
                      <div className="input-group">
                        <span className="input-group-addon">R$</span>
                        <input
                          type="text"
                          className="form-control"
                          readOnly
                          value={formatCurrencyValue(complement.value)}
                        />
                      </div>
                    </div>
                    <div className="form-group">
                      <label>{t("fueling.form.complementUnitValue")}</label>
                      <div className="input-group">
                        <span className="input-group-addon">R$</span>
                        <input
                          type="text"
                          className="form-control"
                          readOnly
                          value={formatCurrencyValue(complement.unitValue)}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="form-group">
              <label className="margin-0 margin-bottom-5" htmlFor="branch">
                {t("fueling.form.branch")}
              </label>
              <Select
                id="branch"
                disabled
                options={[{ id: branchId || "detail-branch", label: d.branchName || "-" }]}
                value={[branchId || "detail-branch"]}
                onChange={noop}
                placeholder={t("common.placeholder.select")}
                className="rounded-small flex-1-1"
              />
            </div>
            <div className="form-group">
              <label className="margin-0 margin-bottom-5" htmlFor="segment">
                {t("fueling.form.segment")}
              </label>
              <Select
                id="segment"
                disabled
                options={[{ id: segId || "detail-seg", label: d.segmentBussinessName || "-" }]}
                value={[segId || "detail-seg"]}
                onChange={noop}
                placeholder={t("common.placeholder.select")}
                className="rounded-small flex-1-1"
              />
            </div>
            <div className="form-group">
              <label className="margin-0 margin-bottom-5" htmlFor="supplier">
                {t("fueling.form.supplier")}
              </label>
              <Select
                id="supplier"
                disabled
                options={[
                  {
                    id: supId || "detail-sup",
                    label: d.supplierClientName || d.clientName || "-",
                  },
                ]}
                value={[supId || "detail-sup"]}
                onChange={noop}
                placeholder={t("common.placeholder.select")}
                className="rounded-small flex-1-1"
              />
            </div>
            <div className="form-group">
              <label className="margin-0 margin-bottom-5" htmlFor="vehicle">
                {t("fueling.form.vehicle")}
              </label>
              <Select
                id="vehicle"
                disabled
                options={[{ id: vehId || "detail-veh", label: d.vehiclePlate || "-" }]}
                value={[vehId || "detail-veh"]}
                onChange={noop}
                placeholder={t("common.placeholder.select")}
                className="rounded-small flex-1-1"
              />
            </div>

            {driverIds.length > 0 ? (
              driverIds.map((driverId: string, index: number) => {
                const driverLabel = t(resolveDriverLabelKey(index));
                const driverName = driverNames[index] || d.driverName || "-";
                return (
                  <div className="form-group" key={`${driverId}-${driverName}`}>
                    <label className="margin-0 margin-bottom-5">{driverLabel}</label>
                    <Select
                      disabled
                      options={[{ id: driverId || `detail-drv-${index}`, label: driverName }]}
                      value={[driverId || `detail-drv-${index}`]}
                      onChange={noop}
                      placeholder={t("common.placeholder.select")}
                      className="rounded-small flex-1-1"
                    />
                  </div>
                );
              })
            ) : (
              <div className="form-group">
                <label className="margin-0 margin-bottom-5" htmlFor="driver">
                  {t("fueling.form.driver1")}
                </label>
                <Select
                  id="driver"
                  disabled
                  options={[{ id: "detail-drv", label: d.driverName || "-" }]}
                  value={["detail-drv"]}
                  onChange={noop}
                  placeholder={t("common.placeholder.select")}
                  className="rounded-small flex-1-1"
                />
              </div>
            )}

            <div className="form-group">
              <label htmlFor="paymentMethod">{t("fueling.form.paymentMethod")}</label>
              <Select
                id="paymentMethod"
                disabled
                options={PAYMENT_METHODS}
                value={paymentMethod ? [mapPaymentLabel(paymentMethod)] : []}
                onChange={noop}
                placeholder={t("common.placeholder.select")}
              />
            </div>
            <div className="form-group">
              <label htmlFor="inputObservation">{t("fueling.form.observation")}</label>
              <textarea
                id="inputObservation"
                className="form-control"
                rows={3}
                readOnly
                placeholder={t("common.label.typeHere")}
                value={d.supplyTrucker?.notes ?? d.notes ?? d.description ?? ""}
              />
            </div>
            <div className="form-group margin-top-20">
              <label className="display-block margin-bottom-5">{t("fueling.dialog.receiptAttached")}</label>
              {d.fileNameAttachmentUrl || d.attachmentUrl ? (
                <span
                  className="btn btn-muted btn-sm rounded-small align-items-center gap-10"
                  style={{
                    display: "inline-flex",
                    maxWidth: "100%",
                    width: "fit-content",
                    boxSizing: "border-box",
                  }}
                >
                  <span
                    style={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {t("common.label.attachmentIndex", { n: 1 })}
                  </span>
                  <button
                    type="button"
                    tabIndex={0}
                    title={t("fueling.dialog.downloadReceipt")}
                    className="rioglyph rioglyph-download cursor-pointer text-color-primary"
                    style={{ flex: "0 0 auto", background: "none", border: "none", padding: 0 }}
                    onClick={() => transactionId && onDownloadFile(transactionId)}
                    onKeyDown={(evento) => {
                      if (evento.key === "Enter" || evento.key === " ") {
                        evento.preventDefault();
                        if (transactionId) onDownloadFile(transactionId);
                      }
                    }}
                  />
                </span>
              ) : (
                <input type="text" className="form-control" readOnly value={t("fueling.dialog.noReceipt")} />
              )}
            </div>
          </div>
        </fieldset>
      </form>
    </div>
  );
};

const FuelingScreen = () => {
  const t = useT();
  const { locale } = useIntl();
  const momentLocale = resolveMomentLocale(locale);
  moment.locale(momentLocale);
  const dispatch = useAppDispatch();

  const startDate = useGetStoredStartDate();
  const endDate = useGetStoredEndDate();
  const kpiPeriodRange = formatPeriodRange(startDate, endDate);
  const isTreeReady = useIsTreeReady();
  const storedFilters = useStoredFilterValues();

  const ALERT_TYPE_MAP: Record<string, { id: string; label: string }> = {
    ABOVE_TANK_CAPACITY: {
      id: "tank-capacity-exceeded",
      label: t("fueling.alert.aboveTankCapacity"),
    },
    ABOVE_FLEET_AVG_PRICE: {
      id: "fuel-price-above-average",
      label: t("fueling.alert.aboveFleetAvgPrice"),
    },
    ABNORMAL_CONSUMPTION: {
      id: "abnormal-consumption",
      label: t("fueling.alert.abnormalConsumption"),
    },
  };

  const [currentPage, setCurrentPage] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);
  const [isLoadingTable, setIsLoadingTable] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [tableData, setTableData] = useState<FuelingRecord[]>([]);
  const [summary, setSummary] = useState<FuelingSummary | null>(null);
  const [isLoadingSummary, setIsLoadingSummary] = useState(true);
  const [detailModal, setDetailModal] = useState<{
    open: boolean;
    data: any;
    loading: boolean;
    id: string | null;
  }>({
    open: false,
    data: null,
    loading: false,
    id: null,
  });
  const [alertsData, setAlertsData] = useState<RecentAlertsSummary | undefined>(undefined);

  const { showDateRangePicker, setShowDateRangePicker, currentMonth, goToPrevMonth, goToNextMonth } =
    useMonthNavigation({ startDate, endDate, momentLocale, dispatch });

  const requestFilters = useRequestFilters(storedFilters, startDate, endDate);

  const fetchAlertTotals = useCallback(async () => {
    try {
      const response = await getFuelingAlertTotals();
      const items = Object.entries(response.alerts)
        .filter(([key]) => ALERT_TYPE_MAP[key])
        .map(([key, count]) => ({
          ...ALERT_TYPE_MAP[key],
          count,
        }));

      setAlertsData({
        totalCount: response.total,
        items,
      });
    } catch (error) {
      console.error("Error fetching alert totals:", error);
    }
    // Depende de locale para re-traduzir os labels (ALERT_TYPE_MAP usa t())
    // quando o idioma muda, já que o resultado fica em cache em alertsData.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locale]);

  useEffect(() => {
    fetchAlertTotals();
  }, [fetchAlertTotals]);

  const fetchSummary = useCallback(async () => {
    const clientIds = [...(storedFilters.selectedClients ?? []), ...(storedFilters.selectedSuppliers ?? [])];
    setIsLoadingSummary(true);
    try {
      const [summaryResult, efficiencyResult] = await Promise.all([
        getFuelingSummary(startDate, endDate, {
          branchIds: storedFilters.selectedBranches?.length ? storedFilters.selectedBranches : undefined,
          segmentIds: storedFilters.selectedSegments?.length ? storedFilters.selectedSegments : undefined,
          clientIds: clientIds.length ? clientIds : undefined,
          vehicleIds: storedFilters.selectedVehicles?.length ? storedFilters.selectedVehicles : undefined,
          driverIds: storedFilters.selectedDrivers?.length ? storedFilters.selectedDrivers : undefined,
          includeNullBranch: storedFilters.includeNullBranch,
          includeNullSegment: storedFilters.includeNullSegment,
          includeNullClient: storedFilters.includeNullClient,
          includeNullSupplier: storedFilters.includeNullSupplier,
          includeNullVehicle: storedFilters.includeNullVehicle,
          includeNullDriver: storedFilters.includeNullDriver,
        }),
        getFuelingEfficiency(5),
      ]);

      setSummary({
        ...summaryResult,
        topVehiclesMostEconomical: efficiencyResult.mostEconomic,
        topVehiclesLeastEconomical: efficiencyResult.leastEconomic,
      });
    } catch (error) {
      console.error("Erro ao carregar resumo de abastecimentos:", error);
      setSummary(null);
    } finally {
      setIsLoadingSummary(false);
    }
  }, [endDate, storedFilters, startDate]);

  const fetchTableData = useCallback(
    async (page: number, append = false) => {
      const setLoading = append ? setIsLoadingMore : setIsLoadingTable;
      setLoading(true);

      try {
        const response = await getFuelingData(
          startDate,
          endDate,
          { page, size: PAGE_SIZE },
          toOptionalListFilters(storedFilters),
          t
        );

        const content = Array.isArray(response?.content) ? response.content : [];
        setTableData((prev) => (append ? [...prev, ...content] : content));
        setTotalRecords(response?.totalElements ?? content.length);
        setCurrentPage(page);
      } catch {
        Notification.error(t("fueling.notification.loadError"));
      } finally {
        setLoading(false);
      }
    },
    [endDate, storedFilters, startDate, t]
  );

  const fetchInitial = useCallback(async () => {
    await Promise.allSettled([fetchSummary(), fetchTableData(0)]);
  }, [fetchSummary, fetchTableData]);

  useEffect(() => {
    if (!isTreeReady) return;

    setCurrentPage(0);
    setTableData([]);
    setTotalRecords(0);
    fetchInitial();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isTreeReady,
    startDate,
    endDate,
    storedFilters.selectedBranches,
    storedFilters.selectedSegments,
    storedFilters.selectedClients,
    storedFilters.selectedSuppliers,
    storedFilters.selectedVehicles,
    storedFilters.selectedDrivers,
  ]);

  const handleLoadMore = () => {
    if (isLoadingMore || tableData.length >= totalRecords) {
      return;
    }
    fetchTableData(currentPage + 1, true);
  };

  const handleDownload = async () => {
    try {
      await exportFueling(startDate, endDate);
      Notification.success(t("fueling.notification.exportSuccess"));
    } catch {
      Notification.error(t("fueling.notification.exportError"));
    }
  };

  const handleViewDetail = async (id: string) => {
    setDetailModal((prev) => ({ ...prev, open: true, loading: true, data: null, id }));
    try {
      const response = await getDetailTransaction(id);
      const transaction = response?.data ?? response;
      setDetailModal((prev) => ({ ...prev, data: transaction ?? null, loading: false }));
    } catch {
      Notification.error(t("fueling.notification.detailError"));
      setDetailModal({ open: false, data: null, loading: false, id: null });
    }
  };

  const handleCloseDetail = () => {
    setDetailModal({ open: false, data: null, loading: false, id: null });
  };

  const handleDownloadFile = async (transactionId: string) => {
    try {
      const response = await getFile(transactionId);
      window.open(response.data.downloadUrl, "_blank");
      Notification.success(t("common.notification.downloadSuccess"));
    } catch {
      Notification.error(t("common.notification.downloadError"));
    }
  };

  let detailBody: ReactNode = null;
  if (detailModal.loading) {
    detailBody = <DetailFormSkeleton rows={16} splitRows={[2]} tallRows={[14]} />;
  } else if (detailModal.data) {
    detailBody = (
      <FuelingDetailForm
        data={detailModal.data}
        momentLocale={momentLocale}
        transactionId={detailModal.id}
        onDownloadFile={handleDownloadFile}
      />
    );
  }

  return (
    <div className="padding-10 padding-20-sm display-flex flex-column gap-10 gap-20-sm min-height-100pct">
      <MonthNavigation
        endDate={endDate}
        startDate={startDate}
        currentMonth={currentMonth}
        onPrevMonth={goToPrevMonth}
        onNextMonth={goToNextMonth}
        onEndDateChange={(date) => dispatch(setEndDate(date))}
        onStartDateChange={(date) => dispatch(setStartDate(date))}
        showDateRangePicker={showDateRangePicker}
        onClick={() => setShowDateRangePicker(!showDateRangePicker)}
        onCloseDateRangePicker={() => setShowDateRangePicker(false)}
      />
      <SelectedFilters requestFilters={requestFilters} />
      <div
        className="display-grid grid-cols-1 grid-cols-2-lg gap-10 gap-20-sm"
        style={{ gridTemplateColumns: "1fr 1fr" }}
      >
        <AlertsRecentCard data={alertsData} />
        {isLoadingSummary ? (
          <KpiCardsSkeleton
            className="display-grid grid-cols-1 grid-cols-2-sm gap-10"
            style={{ gridTemplateColumns: "repeat(2, 1fr)" }}
            contentGapClassName="gap-10 gap-20-sm"
          />
        ) : (
          <div
            className="display-grid grid-cols-1 grid-cols-2-sm gap-10"
            style={{ gridTemplateColumns: "repeat(2, 1fr)" }}
          >
            <KpiCard
              label={
                <>
                  {t("fueling.kpi.totalExpense")}
                  <KpiInfoTooltip
                    title={t("fueling.kpi.totalExpense")}
                    description={t("fueling.kpi.totalExpense.tooltip")}
                    lines={[`R$ ${formatKpiCurrency(summary?.totalExpense)}`]}
                    periodRange={kpiPeriodRange}
                  />
                </>
              }
              value={formatKpiCurrency(summary?.totalExpense)}
              icon={<KpiCardIcon />}
              contentGapClassName="gap-10 gap-20-sm"
              footer={
                <KpiComparisonTooltip comparison={summary?.totalExpenseKpi} formatValue={formatKpiComparisonCurrency}>
                  <KpiCardDelta
                    deltaPercentage={
                      summary?.totalExpenseKpi
                        ? summary?.totalExpenseKpi.variationPercent
                        : summary?.totalExpenseVsPreviousMonthPercentage
                    }
                    vsLabel={t("fueling.kpi.vsLastMonth")}
                  />
                </KpiComparisonTooltip>
              }
            />

            <KpiCard
              label={
                <>
                  {t("fueling.kpi.totalArla32")}
                  <KpiInfoTooltip
                    title={t("fueling.kpi.totalArla32")}
                    description={t("fueling.kpi.totalArla32.tooltip")}
                    lines={[`R$ ${formatKpiCurrency(summary?.arla32Expense)}`]}
                    periodRange={kpiPeriodRange}
                    placement="bottom-end"
                  />
                </>
              }
              value={formatKpiCurrency(summary?.arla32Expense)}
              icon={<KpiCardIcon />}
              contentGapClassName="gap-10 gap-20-sm"
              footerGapClassName="gap-15"
              footer={
                <>
                  <span className="display-flex align-items-center">
                    <KpiComparisonTooltip
                      comparison={summary?.arla32ExpenseKpi}
                      formatValue={formatKpiComparisonCurrency}
                      placement="bottom-end"
                    >
                      <KpiCardDelta
                        deltaPercentage={
                          summary?.arla32ExpenseKpi
                            ? summary.arla32ExpenseKpi.variationPercent
                            : summary?.complementValueARLATotalVsPreviousMonthPercentage
                        }
                        vsLabel={t("fueling.kpi.vsLastMonth")}
                      />
                    </KpiComparisonTooltip>
                  </span>
                  <span className="text-color-gray">
                    {t("fueling.kpi.totalVolume")}
                    <span className="text-medium text-color-success">
                      {" "}
                      {(summary?.arla32Volume ?? 0).toLocaleString("pt-BR")} L
                    </span>
                  </span>
                </>
              }
            />

            <KpiCard
              label={
                <>
                  {t("fueling.kpi.totalFuel")}
                  <KpiInfoTooltip
                    title={t("fueling.kpi.totalFuel")}
                    description={t("fueling.kpi.totalFuel.tooltip")}
                    lines={[`R$ ${formatKpiCurrency(summary?.fuelExpense)}`]}
                    periodRange={kpiPeriodRange}
                  />
                </>
              }
              value={formatKpiCurrency(summary?.fuelExpense)}
              icon={<KpiCardIcon />}
              contentGapClassName="gap-10 gap-20-sm"
              footer={
                <KpiComparisonTooltip comparison={summary?.fuelExpenseKpi} formatValue={formatKpiComparisonCurrency}>
                  <KpiCardDelta
                    deltaPercentage={
                      summary?.fuelExpenseKpi
                        ? summary?.fuelExpenseKpi.variationPercent
                        : summary?.fuelTotalPriceVsPreviousMonthPercentage
                    }
                    vsLabel={t("fueling.kpi.vsLastMonth")}
                  />
                </KpiComparisonTooltip>
              }
            />

            <KpiCard
              label={
                <>
                  {t("fueling.kpi.avgExpense")}
                  <KpiInfoTooltip
                    title={t("fueling.kpi.avgExpense")}
                    description={t("fueling.kpi.avgExpense.tooltip")}
                    lines={[
                      t("fueling.kpi.avgExpense.formula"),
                      `R$ ${formatKpiCurrency(summary?.totalExpense)} ÷ ${summary?.supplyTotal ?? 0} = R$ ${formatKpiCurrency(
                        summary?.averageFleetConsumption
                      )}`,
                    ]}
                    periodRange={kpiPeriodRange}
                    placement="bottom-end"
                  />
                </>
              }
              value={formatKpiCurrency(summary?.averageFleetConsumption)}
              icon={<KpiCardIcon />}
              contentGapClassName="gap-10 gap-20-sm"
              footerGapClassName="gap-15"
              footer={
                <KpiComparisonTooltip
                  comparison={summary?.averageFleetConsumptionKpi}
                  formatValue={formatKpiComparisonCurrency}
                  placement="bottom-end"
                >
                  <span className="text-color-gray">
                    {t("fueling.kpi.supplyCount")}
                    <span className="text-medium text-color-primary"> {summary?.supplyTotal ?? 0}</span>
                  </span>
                  <span className="text-color-gray">
                    {t("fueling.kpi.vehicleCount")}
                    <span className="text-medium text-color-primary"> {summary?.fleetVehicleTotal ?? 0}</span>
                  </span>
                </KpiComparisonTooltip>
              }
            />
          </div>
        )}
      </div>

      <div
        className="display-grid grid-cols-1 grid-cols-2-lg gap-10 gap-20-sm"
        style={{ gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)" }}
      >
        {isLoadingSummary ? <FuelMixChartSkeleton /> : summary && <FuelMixChart summary={summary} />}
        {isLoadingSummary ? <Top5VehiclesTableSkeleton /> : summary && <Top5VehiclesTable summary={summary} />}
      </div>

      {/* Table */}
      <div className="flex-1-1 height-100pct">
        <TableFueling
          data={tableData}
          totalData={totalRecords}
          viewType="TABLE"
          onLoadMore={handleLoadMore}
          handleRefresh={fetchInitial}
          handleDownload={handleDownload}
          onViewDetail={handleViewDetail}
          isLoadingMore={isLoadingMore}
          isLoadingTable={isLoadingTable}
        />

        <Dialog
          show={detailModal.open}
          title={<span className="text-medium text-size-16">{t("fueling.dialog.detailTitle")}</span>}
          body={detailBody}
          footer={
            <div className="display-flex flex-wrap gap-10 justify-content-end">
              <button className="btn btn-default width-100pct-xs" onClick={handleCloseDetail}>
                {t("common.action.close")}
              </button>
            </div>
          }
          onClose={handleCloseDetail}
          showCloseButton
          bsSize="md"
        />
      </div>
    </div>
  );
};

const renderFuelMixBarLabel = (props: any) => {
  const { x, y, width, value } = props;
  if (!value || value <= 0) return <></>;
  const formattedValue = `R$ ${value.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  return (
    <text
      x={x + width / 2}
      y={y - 17}
      fill="#808080"
      fontSize={11}
      fontWeight={500}
      textAnchor="middle"
      style={{ whiteSpace: "nowrap" }}
    >
      {formattedValue}
    </text>
  );
};

const renderFuelMixBarShape = (props: any) => {
  const { x, y, width, height, payload } = props;
  return <rect x={x} y={y} width={width} height={height} rx={4} ry={4} fill={payload.fill} />;
};

const createFuelMixTooltipContent = (t: Translate) => (props: any) => {
  if (!props.active || !props.payload || props.payload.length === 0) return null;
  const data = props.payload[0].payload as FuelMixDataItem;
  return (
    <div className="display-flex flex-column gap-5 padding-y-10 padding-x-15 rounded border bg-lightest text-center text-size-12">
      <span>
        {t("fueling.chart.tooltip.totalExpense")} R${" "}
        {data.expense.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
      </span>
      <span>
        {t("fueling.chart.tooltip.volumeFueled")}{" "}
        {data.volume.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {data.unit}
      </span>
      <span>
        {t("fueling.chart.tooltip.avgUnitPrice")} R${" "}
        {data.avgPrice.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}/{data.unit}
      </span>
    </div>
  );
};

function FuelMixChart({ summary }: Readonly<{ summary: FuelingSummary }>) {
  const t = useT();

  const chartData = useMemo<FuelMixDataItem[]>(() => {
    const fuelTypes = summary.fuelTypes ?? {};
    return FUEL_ORDER.map((key) => {
      const detail = fuelTypes[key as keyof typeof fuelTypes];
      const expense = detail?.expense ?? 0;
      const volume = detail?.quantity ?? 0;
      const avgPrice = volume > 0 ? expense / volume : 0;
      return {
        name: t(`fueling.fuel.${key}`),
        key,
        expense,
        volume,
        unit: FUEL_UNIT_MAP[key],
        avgPrice,
        fill: BAR_COLOR_MAP[key],
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [summary, t]);

  return (
    <Card className="padding-y-25 padding-x-15 padding-x-25-sm">
      <div className="padding-bottom-20">
        <span className="text-size-16 text-bold">{t("fueling.chart.fuelMix")}</span>
      </div>
      <div className="width-100pct">
        <ComposedChart
          height={280}
          data={chartData}
          margin={{ top: 30, right: 15, left: 15, bottom: 15 }}
          bars={[
            <Bar
              key="expense"
              dataKey="expense"
              barSize={50}
              radius={[4, 4, 0, 0]}
              label={renderFuelMixBarLabel}
              shape={renderFuelMixBarShape}
            />,
          ]}
          lines={[
            <Line
              key="expense-line"
              type="monotone"
              dataKey="expense"
              strokeWidth={2}
              stroke="#30B4C0"
              dot={{ fill: "#fff", r: 5, stroke: "#30B4C0", strokeWidth: 2 }}
              activeDot={{ fill: "#30B4C0", r: 6, stroke: "none" }}
            />,
          ]}
          tooltip={<ChartTooltip content={createFuelMixTooltipContent(t)} />}
          showXAxis
          showYAxis={false}
          xAxisOptions={{
            dataKey: "name",
            axisLine: { stroke: "#E0E0E0", strokeWidth: 1 },
            tickLine: false,
            padding: { left: 0, right: 0 },
          }}
          containerOptions={{ width: "100%", height: 280 }}
        />
      </div>
    </Card>
  );
}

function Top5VehiclesTable({ summary }: Readonly<{ summary: FuelingSummary }>) {
  const t = useT();
  const [showMostEconomical, setShowMostEconomical] = useState(true);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const vehicles = showMostEconomical
    ? (summary.topVehiclesMostEconomical ?? [])
    : (summary.topVehiclesLeastEconomical ?? []);

  const normalizeFuelType = (fuelType: string) => {
    if (!fuelType) return "-";
    const key = fuelType.toLowerCase();
    if (FUEL_ORDER.includes(key)) return t(`fueling.fuel.${key}`);
    return key.charAt(0).toUpperCase() + key.slice(1);
  };

  return (
    <Card className="padding-y-25 padding-x-15 padding-x-25-sm">
      <div className="display-flex flex-wrap justify-content-between align-items-center padding-bottom-20">
        <div className="display-flex flex-wrap align-items-center gap-10 min-width-0">
          <span className="text-size-16 text-bold">{t("fueling.chart.top5vehicles")}</span>
          <span
            style={{
              fontFamily: "Source Sans Pro",
              fontWeight: 300,
              fontStyle: "italic",
              fontSize: 14,
              lineHeight: "22.86px",
              letterSpacing: 0,
              verticalAlign: "middle",
            }}
          >
            {showMostEconomical ? t("fueling.chart.mostEconomical") : t("fueling.chart.leastEconomical")}
          </span>
        </div>
        <div style={{ position: "relative" }}>
          <button
            className="btn btn-sm"
            style={{
              backgroundColor: "transparent",
              border: "none",
              padding: 4,
              cursor: "pointer",
            }}
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            <span className="rioglyph rioglyph-filter" style={{ fontSize: 18, color: "#30B4C0" }} />
          </button>
          {isMenuOpen && (
            <>
              <button
                type="button"
                aria-label={t("common.action.close")}
                tabIndex={0}
                style={{
                  position: "fixed",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  zIndex: 999,
                  background: "transparent",
                  border: "none",
                  padding: 0,
                }}
                onClick={() => setIsMenuOpen(false)}
                onKeyDown={(evento) => {
                  if (evento.key === "Enter" || evento.key === " ") {
                    evento.preventDefault();
                    setIsMenuOpen(false);
                  }
                }}
              />
              <div
                style={{
                  position: "absolute",
                  top: "100%",
                  right: 0,
                  marginTop: 8,
                  backgroundColor: "var(--color-white)",
                  border: "1px solid var(--color-lighter)",
                  borderRadius: 4,
                  boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                  minWidth: 180,
                  zIndex: 1000,
                  padding: "8px 0",
                }}
              >
                <button
                  style={{
                    width: "100%",
                    padding: "8px 16px",
                    border: "none",
                    backgroundColor: "transparent",
                    textAlign: "left",
                    cursor: "pointer",
                    fontSize: 14,
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                  }}
                  onClick={() => {
                    setShowMostEconomical(true);
                    setIsMenuOpen(false);
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "var(--color-lightest)")}
                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
                >
                  <span style={{ color: "#4CAF50" }}>⬆</span>
                  {t("fueling.chart.mostEconomical")}
                </button>
                <button
                  style={{
                    width: "100%",
                    padding: "8px 16px",
                    border: "none",
                    backgroundColor: "transparent",
                    textAlign: "left",
                    cursor: "pointer",
                    fontSize: 14,
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                  }}
                  onClick={() => {
                    setShowMostEconomical(false);
                    setIsMenuOpen(false);
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "var(--color-lightest)")}
                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
                >
                  <span style={{ color: "#FF5722" }}>⬇</span>
                  {t("fueling.chart.leastEconomical")}
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      <div className="table-responsive">
        <table className="table table-hover" style={{ marginBottom: 0 }}>
          <thead>
            <tr className="bg-lightest">
              <th className="text-color-gray" style={{ fontWeight: 500, fontSize: 13, whiteSpace: "nowrap" }}>
                {t("fueling.table.header.vehicle")}
              </th>
              <th className="text-color-gray" style={{ fontWeight: 500, fontSize: 13 }}>
                {t("fueling.table.header.fuel")}
              </th>
              <th className="text-color-gray" style={{ fontWeight: 500, fontSize: 13 }}>
                {t("fueling.table.header.consumptionUnit")}
              </th>
              <th className="text-color-gray" style={{ fontWeight: 500, fontSize: 13 }}>
                {t("fueling.table.header.consumptionCurrency")}
              </th>
            </tr>
          </thead>
          <tbody>
            {vehicles.length === 0 ? (
              <tr>
                <td colSpan={4} className="text-center text-color-gray padding-20">
                  {t("common.label.noData")}
                </td>
              </tr>
            ) : (
              vehicles.map((vehicle) => (
                <tr key={`${vehicle.vehiclePlate}-${vehicle.fuelType}`}>
                  <td style={{ fontSize: 13, whiteSpace: "nowrap" }}>{vehicle.vehiclePlate}</td>
                  <td style={{ fontSize: 13 }}>{normalizeFuelType(vehicle.fuelType)}</td>
                  <td style={{ fontSize: 13 }}>
                    {vehicle.consumptionValue.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}{" "}
                    {vehicle.consumptionUnit}
                  </td>
                  <td style={{ fontSize: 13 }}>
                    R$ {vehicle.consumptionCurrency.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

export default FuelingScreen;
